/**
 * Time Role - Validate Pillar
 *
 * Validação de valores de tempo/duração.
 * ZERO dependências de outros pilares - totalmente standalone.
 *
 * @example
 * import { validateTime, isValidTime } from '@attrx/role-morphic/time/validate';
 *
 * validateTime(100);   // { valid: true, errors: [] }
 * validateTime(-5);    // { valid: false, errors: ['Time cannot be negative'] }
 * isValidTime(100);    // true
 */

// =============================================================================
// TYPES
// =============================================================================

export interface TimeValidationConfig {
  /** Valor mínimo permitido (default: 0) */
  min?: number;
  /** Valor máximo permitido */
  max?: number;
  /** Mensagem de erro para valor abaixo do mínimo */
  minError?: string;
  /** Mensagem de erro para valor acima do máximo */
  maxError?: string;
  /** Permitir valores negativos (default: false) */
  allowNegative?: boolean;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

// =============================================================================
// DEFAULT CONFIG
// =============================================================================

export const TIME_VALIDATION_CONFIG: TimeValidationConfig = {
  min: 0,
  minError: "Time cannot be negative",
  allowNegative: false,
};

// =============================================================================
// VALIDATE
// =============================================================================

/**
 * Valida um valor de tempo
 *
 * @param value - Valor a validar
 * @param config - Configuração de validação (opcional)
 * @returns Resultado da validação com lista de erros
 *
 * @example
 * validateTime(100);                    // { valid: true, errors: [] }
 * validateTime(-5);                     // { valid: false, errors: ['Time cannot be negative'] }
 * validateTime(-5, { allowNegative: true }); // { valid: true, errors: [] }
 */
export function validateTime(
  value: unknown,
  config: TimeValidationConfig = TIME_VALIDATION_CONFIG,
): ValidationResult {
  const errors: string[] = [];

  // Must be a number
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return {
      valid: false,
      errors: ["Value must be a finite number"],
    };
  }

  // Merge with defaults
  const effectiveConfig = { ...TIME_VALIDATION_CONFIG, ...config };

  // Check negative
  if (!effectiveConfig.allowNegative && value < 0) {
    errors.push(effectiveConfig.minError || "Time cannot be negative");
  }

  // Check min (if allowNegative is true and min is set)
  if (
    effectiveConfig.allowNegative &&
    effectiveConfig.min !== undefined &&
    value < effectiveConfig.min
  ) {
    errors.push(effectiveConfig.minError || `Value must be at least ${effectiveConfig.min}`);
  }

  // Check max
  if (effectiveConfig.max !== undefined && value > effectiveConfig.max) {
    errors.push(effectiveConfig.maxError || `Value must be at most ${effectiveConfig.max}`);
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Verifica se valor de tempo é válido (helper booleano)
 *
 * @param value - Valor a validar
 * @param config - Configuração de validação (opcional)
 * @returns true se válido, false caso contrário
 *
 * @example
 * isValidTime(100);  // true
 * isValidTime(-5);   // false
 */
export function isValidTime(
  value: unknown,
  config?: TimeValidationConfig,
): boolean {
  return validateTime(value, config).valid;
}
